import { ChatsApi } from "@/api/chats";
import { useAccess } from "@/hooks";
import { useGetMeQuery } from "@/queries";
import { LocalStorageService, ResourceName } from "@/utils";
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";
import {
  supportContextInitialState,
  supportContextReducer,
} from "./support.state";
import {
  FeedbackTab,
  SupportContextAction,
  SupportContextState,
} from "./support.types";

export interface SupportContextProps extends SupportContextState {
  readonly dispatch: Dispatch<SupportContextAction>;
}

const SupportContext = createContext<SupportContextProps>({
  dispatch: () => "",
  feedbackTab: FeedbackTab.NEW_FEEDBACKS,
  feedbackFilters: {
    search: "",
  },
});

const SupportContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { canView } = useAccess();
  const { me } = useGetMeQuery();

  const [state, dispatch] = useReducer(
    supportContextReducer,
    supportContextInitialState
  );

  useEffect(() => {
    if (
      !me &&
      !canView(ResourceName.SUPPORT) &&
      LocalStorageService.getSupportAccessToken()
    ) {
      return;
    }

    const authenticateAsSupport = async () => {
      try {
        const supportAuthTokens = await ChatsApi.authenticateAsSupport();

        LocalStorageService.setSupportAccessToken(
          supportAuthTokens.accessToken
        );
        LocalStorageService.setSupportRefreshToken(
          supportAuthTokens.refreshToken
        );
      } catch (error) {
        console.error(error);
      }
    };

    authenticateAsSupport();
  }, [me?.id]);

  return (
    <SupportContext.Provider value={{ ...state, dispatch }}>
      {children}
    </SupportContext.Provider>
  );
};

const useSupportContext = () => useContext(SupportContext);

// eslint-disable-next-line react-refresh/only-export-components
export { SupportContext, SupportContextProvider, useSupportContext };
